import type { ColumnDef } from '@tanstack/react-table';
import { CalendarClock, Plus } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipment } from '@/features/equipment/queries';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { useMaintenancePlans } from '@/features/maintenance-plans/queries';
import type {
  MaintenancePlan,
  WorkOrderType,
} from '@/features/maintenance-plans/types';
import type { ListMaintenancePlansFilters } from '@/features/maintenance-plans/service';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: 6 }).map((__, j) => (
            <Skeleton key={j} className="h-9 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function MaintenancePlansListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Build the filter bag from URL params. isActive is special: only
  // include it when the user explicitly chose 'true'/'false' — leaving
  // the dropdown at "All" omits the filter entirely.
  const filters: ListMaintenancePlansFilters = (() => {
    const f: ListMaintenancePlansFilters = {};
    const v = searchParams.get('vesselId');
    if (v) f.vesselId = v;
    const et = searchParams.get('equipmentTypeId');
    if (et) f.equipmentTypeId = et;
    const a = searchParams.get('isActive');
    if (a === 'true') f.isActive = true;
    if (a === 'false') f.isActive = false;
    return f;
  })();

  const { data, isLoading, isError, error, refetch, isFetching } =
    useMaintenancePlans(filters);

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list.filter((p) =>
      matchesQuery(p, deferredSearch, [
        (x) => x.title,
        (x) => x.description,
        (x) => x.type,
      ]),
    );
  }, [data, deferredSearch]);
  const { data: vessels } = useVessels();
  const { data: equipmentTypes } = useEquipmentTypes();
  const { data: equipment } = useEquipment(
    filters.vesselId ? { vesselId: filters.vesselId } : {},
  );

  const vesselNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of vessels ?? []) m.set(v.id, vesselDisplayName(v));
    return m;
  }, [vessels]);
  const equipmentTypeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const et of equipmentTypes ?? []) m.set(et.id, et.name);
    return m;
  }, [equipmentTypes]);
  const equipmentTagById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of equipment ?? []) m.set(e.id, e.tag);
    return m;
  }, [equipment]);

  const setFilter = (key: string, next: string | null) => {
    if (next) searchParams.set(key, next);
    else searchParams.delete(key);
    setSearchParams(searchParams, { replace: true });
  };

  const columns = useMemo<ColumnDef<MaintenancePlan>[]>(
    () => [
      {
        accessorKey: 'title',
        header: () => t('maintenancePlan.columns.title'),
        cell: ({ row }) => (
          <Link
            to={`/maintenance-plans/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-primary hover:underline"
          >
            {row.original.title}
          </Link>
        ),
      },
      {
        accessorKey: 'type',
        header: () => t('maintenancePlan.columns.type'),
        cell: ({ getValue }) =>
          t(`workOrder.type.${getValue<WorkOrderType>()}`),
      },
      {
        id: 'target',
        header: () => t('maintenancePlan.columns.target'),
        cell: ({ row }) => {
          const p = row.original;
          if (p.equipmentId) {
            return (
              <span className="font-mono text-xs">
                {equipmentTagById.get(p.equipmentId) ?? p.equipmentId.slice(0, 8)}
              </span>
            );
          }
          if (p.equipmentTypeId) {
            return (
              <span className="text-xs">
                {equipmentTypeNameById.get(p.equipmentTypeId) ?? '—'}
              </span>
            );
          }
          return '—';
        },
      },
      {
        id: 'vessel',
        header: () => t('maintenancePlan.columns.vessel'),
        cell: ({ row }) =>
          row.original.vesselId
            ? (vesselNameById.get(row.original.vesselId) ?? '—')
            : t('maintenancePlan.allVessels'),
      },
      {
        accessorKey: 'leadTimeDays',
        header: () => t('maintenancePlan.columns.leadTimeDays'),
        cell: ({ getValue }) => `${getValue<number>()} d`,
      },
      {
        accessorKey: 'isActive',
        header: () => t('maintenancePlan.columns.isActive'),
        cell: ({ getValue }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
              getValue<boolean>()
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-100 text-slate-700',
            )}
          >
            {getValue<boolean>()
              ? t('maintenancePlan.active')
              : t('maintenancePlan.paused')}
          </span>
        ),
      },
    ],
    [t, equipmentTagById, equipmentTypeNameById, vesselNameById],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('maintenancePlan.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('maintenancePlan.list.loadError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">
          {t('maintenancePlan.list.title')}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('maintenancePlan.list.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-64"
            data-testid="maintenance-plans-search"
          />
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('maintenancePlan.list.filterByVessel')}
            </span>
            <VesselSelect
              value={filters.vesselId ?? null}
              onChange={(v) => setFilter('vesselId', v)}
              className="w-48"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('maintenancePlan.list.filterByActive')}
            </span>
            <select
              value={searchParams.get('isActive') ?? ''}
              onChange={(e) => setFilter('isActive', e.target.value || null)}
              className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('maintenancePlan.list.allStatuses')}</option>
              <option value="true">{t('maintenancePlan.active')}</option>
              <option value="false">{t('maintenancePlan.paused')}</option>
            </select>
          </label>
          <Button asChild>
            <Link to="/maintenance-plans/new">
              <Plus aria-hidden />
              {t('maintenancePlan.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('maintenancePlan.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListSkeleton />}
          emptyState={
            <EmptyState
              icon={CalendarClock}
              title={t('maintenancePlan.list.empty')}
              description={t('maintenancePlan.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/maintenance-plans/new">
                    <Plus aria-hidden />
                    {t('maintenancePlan.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(p) => navigate(`/maintenance-plans/${p.id}`)}
        />
      )}
    </div>
  );
}
